{/* Copyright 2026 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. */}

import {Layout} from '../../../src/Layout';
export default Layout;
import {FunctionAPIGroup} from '../../../src/FunctionAPI';
import {InterfaceType} from '../../../src/types';
import docs from 'docs:@react-aria/tooltip';
import statelyDocs from 'docs:@react-stately/tooltip';
import Anatomy from '/packages/dev/s2-docs/pages/react-aria/TooltipAnatomy.svg';

export const section = 'Hooks';
export const description = 'Provides the behavior and accessibility implementation for a tooltip component.';
export const isSubpage = true;

# useTooltipTrigger

<PageDescription>{docs.exports.useTooltipTrigger.description}</PageDescription>

```tsx render files={["starters/hooks/src/Tooltip.tsx", "starters/hooks/src/Tooltip.css", "starters/hooks/src/Button.css"]}
"use client";
import {Button} from 'react-aria-components/Button';
import {TooltipTrigger} from 'hooks-starter/Tooltip';

<div style={{display: 'flex', gap: 8}}>
  <TooltipTrigger tooltip="Edit">
    <Button className="react-aria-Button button-base" data-variant="secondary">Edit</Button>
  </TooltipTrigger>
  <TooltipTrigger tooltip="Delete">
    <Button className="react-aria-Button button-base" data-variant="secondary">Delete</Button>
  </TooltipTrigger>
</div>
```

## API

<Anatomy />

```tsx
<TooltipTrigger>
  <Button />
  <Tooltip>
    <OverlayArrow />
  </Tooltip>
</TooltipTrigger>
```

<FunctionAPIGroup
  functions={[
    {function: statelyDocs.exports.useTooltipTriggerState, links: statelyDocs.links},
    {function: docs.exports.useTooltipTrigger, links: docs.links},
    {function: docs.exports.useTooltip, links: docs.links},
  ]}
/>

### TooltipTriggerState

<InterfaceType {...statelyDocs.exports.TooltipTriggerState} links={statelyDocs.links} />

### TooltipTriggerProps

<InterfaceType {...docs.exports.TooltipTriggerProps} links={docs.links} />

### TooltipTriggerAria

<InterfaceType {...docs.exports.TooltipTriggerAria} links={docs.links} grouped={false} />

### AriaTooltipProps

<InterfaceType {...docs.exports.AriaTooltipProps} links={docs.links} />

### TooltipAria

<InterfaceType {...docs.exports.TooltipAria} links={docs.links} grouped={false} />
